<template>
  <div class="kefu-iframe-modal" v-if="visible">
    <div class="iframe-container" @click.stop>
      <div class="close-btn" @click="close">
        <span class="close-icon">×</span>
      </div>

      <!-- 加载状态 -->
      <div class="loading-container" v-if="isLoading">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ loadingText }}</div>
      </div>

      <iframe
        :src="iframeUrl"
        class="kefu-iframe"
        :class="{ 'iframe-hidden': isLoading }"
        frameborder="0"
        allowfullscreen
        @load="onIframeLoad">
      </iframe>
    </div>
  </div>
</template>

<script>
import { getUser, getUserId, getRemark } from '@/utils/auth'

export default {
  name: 'KefuIframe',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    link: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isLoading: true,
      fallbackTimer: null,
      meiqiaReady: false,
      conversationActive: false
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.isLoading = true
        this.meiqiaReady = false
        this.conversationActive = false
        // 添加消息监听器
        this.addMessageListener()
        // 设置备用超时机制
        this.setFallbackTimer()
      } else {
        // 移除消息监听器
        this.removeMessageListener()
        // 清除备用定时器
        this.clearFallbackTimer()
        // 重置状态
        this.resetStates()
      }
    }
  },
  mounted() {
    // 组件挂载时添加消息监听器
    this.addMessageListener()
  },
  beforeDestroy() {
    // 组件销毁前移除消息监听器
    this.removeMessageListener()
  },
  computed: {
    iframeUrl() {
      if (!this.link) return ''

      // 如果是qiabot链接，注入顾客信息后返回
      if (this.link.includes('qiabot.html')) {
        return this.enhanceQiabotUrl(this.link)
      }

      // 如果链接已经包含chatlink.html，直接使用
      if (this.link.includes('chatlink.html')) {
        // 将客服链接中的域名替换为当前访问的域名
        let kefuLink = this.link
        try {
          const currentHost = window.location.host // 获取当前域名（包含端口）
          const linkUrl = new URL(this.link)
          linkUrl.host = currentHost // 替换域名
          kefuLink = linkUrl.toString()
          console.log("linkUrl", linkUrl)
        } catch (e) {
          console.error('替换客服链接域名失败:', e)
          // 如果替换失败，使用原链接
          kefuLink = this.link
        }
        console.log("kefuLink", kefuLink)
        return this.enhanceChatLinkUrl(kefuLink)
      }

      // 否则构建新的chatlink.html URL
      return this.buildMeiqiaIframeUrl(this.link)
    },
    loadingText() {
      if (!this.meiqiaReady) {
        return this.$t('kefu.initializing')
      } else if (this.meiqiaReady && !this.conversationActive) {
        return this.$t('kefu.connecting')
      } else {
        return this.$t('kefu.loading')
      }
    }
  },
  methods: {
    // 构建美洽iframe URL
    buildMeiqiaIframeUrl(link) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      // 解析原始链接中的entId
      let entId = ''
      let match = link.match(/eid=([^&]+)/)
      if (match) {
        entId = match[1]
      } else {
        match = link.match(/entId=([^&]+)/)
        if (match) {
          entId = match[1]
        }
      }

      // 构建chatlink.html的URL
      const baseUrl = window.location.origin + '/chatlink.html'
      const params = new URLSearchParams({
        entId: entId,
        ID: userId,
        name: userName,
        username: userName,
        remark: remark,
        language: 'en'
      })

      return `${baseUrl}?${params.toString()}`
    },
    // 增强 qiabot.html 链接，注入顾客身份与信息
    enhanceQiabotUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      const urlObj = new URL(url, window.location.origin)

      if (userId) {
        urlObj.searchParams.set('clientid', userId)
      }

      const metadata = {}
      if (userName) metadata.name = userName
      if (remark) metadata.comment = remark
      if (Object.keys(metadata).length > 0) {
        urlObj.searchParams.set('metadata', JSON.stringify(metadata))
      }

      return urlObj.toString()
    },
    // 增强已有的chatlink.html链接，添加用户信息
    enhanceChatLinkUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      // 解析现有URL
      const urlObj = new URL(url, window.location.origin)

      // 添加或更新用户信息参数
      if (userId) urlObj.searchParams.set('ID', userId)
      if (userName) {
        urlObj.searchParams.set('name', userName)
        urlObj.searchParams.set('username', userName)
      }
      if (remark) urlObj.searchParams.set('remark', remark)

      // 确保有语言参数
      if (!urlObj.searchParams.has('language')) {
        urlObj.searchParams.set('language', 'en')
      }

      return urlObj.toString()
    },
    close() {
      this.$emit('close')
    },
    onIframeLoad() {
      // iframe DOM加载完成，但美洽可能还在初始化中
      // 真正的加载完成由美洽的allSet回调通知
      console.log('📄 iframe DOM加载完成，等待美洽初始化...');

      // 如果iframe加载完成但美洽还没准备好，给一个较短的等待时间
      setTimeout(() => {
        if (this.isLoading && !this.meiqiaReady) {
          console.log('⏰ iframe加载完成但美洽未就绪，继续等待美洽回调...');
        }
      }, 2000)
    },
    // 添加消息监听器
    addMessageListener() {
      window.addEventListener('message', this.handleMessage)
    },
    // 移除消息监听器
    removeMessageListener() {
      window.removeEventListener('message', this.handleMessage)
    },
    // 处理来自iframe的消息
    handleMessage(event) {
      if (!event.data || !event.data.type) return;

      console.log('🔔 收到美洽postMessage:', event.data);

      switch (event.data.type) {
        case 'meiqia_ready':
          console.log('✅ 美洽客服初始化完成');
          console.log('📊 消息来源:', event.data.source || 'callback');
          console.log('📊 客服在线状态:', event.data.servability);

          // 标记美洽已就绪
          this.meiqiaReady = true

          // 美洽初始化完成，隐藏加载状态
          this.isLoading = false
          // 清除备用定时器
          this.clearFallbackTimer()

          // 触发自定义事件，通知父组件美洽已就绪
          this.$emit('meiqia-ready', {
            servability: event.data.servability,
            source: event.data.source
          })
          break;

        case 'meiqia_conversation_start':
          console.log('✅ 美洽会话开始');
          this.conversationActive = true
          this.$emit('conversation-start')
          break;

        case 'meiqia_conversation_end':
          console.log('✅ 美洽会话结束');
          this.conversationActive = false
          this.$emit('conversation-end')
          break;

        case 'meiqia_smart_guide_show':
          console.log('✅ 美洽智能引导显示');
          this.$emit('smart-guide-show')
          break;

        case 'meiqia_smart_guide_close':
          console.log('✅ 美洽智能引导关闭');
          this.$emit('smart-guide-close')
          break;

        case 'meiqia_before_close':
          console.log('✅ 美洽准备关闭');
          this.$emit('before-close')
          break;

        case 'meiqia_panel_visibility':
          console.log('✅ 美洽面板可见性变化:', event.data.visibility);
          this.$emit('panel-visibility-change', event.data.visibility)
          break;

        case 'meiqia_conv_click':
          console.log('✅ 美洽对话点击，类型:', event.data.convType, '对话ID:', event.data.convId);
          this.$emit('conversation-click', {
            type: event.data.convType,
            convId: event.data.convId
          })
          break;

        default:
          console.log('🔍 未知的美洽消息类型:', event.data.type);
      }
    },
    // 设置备用超时机制
    setFallbackTimer() {
      this.clearFallbackTimer()
      this.fallbackTimer = setTimeout(() => {
        if (this.isLoading && !this.meiqiaReady) {
          console.log('⚠️ 美洽回调超时，使用备用方案隐藏加载状态');
          console.log('📊 当前状态 - 美洽就绪:', this.meiqiaReady, '会话活跃:', this.conversationActive);
          this.isLoading = false
          // 触发超时事件
          this.$emit('loading-timeout')
        }
      }, 15000) // 15秒超时，给美洽更多时间初始化
    },
    // 清除备用定时器
    clearFallbackTimer() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer)
        this.fallbackTimer = null
      }
    },
    // 重置状态
    resetStates() {
      this.meiqiaReady = false
      this.conversationActive = false
    }
  }
}
</script>

<style lang="less" scoped>
.kefu-iframe-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.iframe-container {
  width: 90%;
  height: 80%;
  max-width: 800px;
  max-height: 600px;
  background: rgba(0, 0, 0, 0.5);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.3);
  position: relative;
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;

  &:hover {
    background: #fff;
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(0.9);
  }

  .close-icon {
    color: #666;
    font-size: 20px;
    font-weight: bold;
    line-height: 1;
    font-family: Arial, sans-serif;
  }
}

.loading-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 5;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.loading-text {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.kefu-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  transition: opacity 0.3s ease;

  &.iframe-hidden {
    opacity: 0;
  }
}

// 移动端适配
@media (max-width: 768px) {
  .iframe-container {
    width: 95%;
    height: 90%;
    max-width: none;
    max-height: none;
  }

  .close-btn {
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;

    .close-icon {
      font-size: 18px;
    }
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border-width: 2px;
    margin-bottom: 12px;
  }

  .loading-text {
    font-size: 14px;
  }
}
</style>
